import { Injectable, OnDestroy } from "@angular/core";
import { HttpService } from "./http.service";
import { GaoUserResponse, User, UserRoleString } from "../models/user";
import {
  AttorneyInfoResponse,
  AttorneyNoteResponse, CaseCompleteState, CompleteBtnData, CompleteAndDMResponse,
  DMNumberCheckResponse,
  SubmitNewDocDTO, ValidateDmInfoResponse, VerifyDmInfoResponse, CaseAccessResponse, JoinValidateResponse, JoinCaseResponse
} from "../models/docket";
import { HttpHeaders, HttpParams } from "@angular/common/http";
import { ProtestInfo } from "../models/protest";
import {BehaviorSubject, catchError, EMPTY, Observable, Subscription, throwError} from "rxjs";
import { SidebarService } from "./sidebar.service";
import { UtilService } from "./util.service";
import { StateService } from "./state.service";
import {Router} from "@angular/router";

@Injectable({
  providedIn: 'root'
})
export class CaseDocketService implements OnDestroy {
  private readonly docketSub = new BehaviorSubject<AttorneyInfoResponse | undefined>(undefined);
  docketSub$ = this.docketSub.asObservable();
  emailSub!: Subscription;
  completeSub!: Subscription;

  constructor(private readonly httpService: HttpService,
              private readonly sidebarService: SidebarService,
              private readonly util: UtilService,
              private readonly stateService: StateService,
              private readonly router: Router) {
    this.emailSub = this.sidebarService.emailSub$.subscribe(() => {
      this.onEmailPref();
    });
    this.completeSub = this.sidebarService.completeSub$.subscribe((data: CompleteBtnData) => {
      this.onComplete(data.text, data.protestInfo);
    });
  }

  ngOnDestroy() {
    this.emailSub.unsubscribe();
    this.completeSub.unsubscribe();
  }

  updateProtestInfo(aNum: string, groupNo: number | undefined, oldValue: any, typeOfchange: string, newValue: any, agency_tier1: number | undefined, agency_tier2: number | undefined) {
    const formData = {
      aNum, oldValue, typeOfchange, newValue, newValue2: groupNo, agency_tier1, agency_tier2
    }

    const httpParams = this.httpService.createHttpParams(formData);
    const options = { headers: this.httpService.formHeaders };
    const url = '/change-protest_Info-attribute/' + typeOfchange;

    return this.httpService.Post<any>(url, httpParams, options);
  }

  getListOfAttorneys() {
    const url = '/get-attorney-list'

    return this.httpService.Get<GaoUserResponse>(url);
  }

  addMinuteEntry(aNum: string, note: string) {
    const formData = {
      attorney_note: note,
      protestId: aNum
    }

    const httpParams = this.httpService.createHttpParams(formData);
    const options = { headers: this.httpService.formHeaders };
    const url = '/add-attachments/minute-entry'

    return this.httpService.Post<SubmitNewDocDTO>(url, httpParams, options);
  }

  addAttorneyNote(note: string, fileId: string) {
    const formData = {
      fileId,
      note
    }

    const httpParams = this.httpService.createHttpParams(formData);
    const options = { headers: this.httpService.formHeaders };
    const url = '/add-attorney-note'

    return this.httpService.Post<AttorneyNoteResponse>(url, httpParams, options);
  }

  addComments(comments: string, file_Id: number) {
    const formData = {
      file_Id,
      newValue: comments
    }

    const httpParams = this.httpService.createHttpParams(formData);
    const options = { headers: this.httpService.formHeaders };
    const url = '/change-file-attribute/comments'

    return this.httpService.Post<AttorneyInfoResponse>(url, httpParams, options);
  }

  changeTypeOfDocument(aNum: string, file_Id: number, docTypeId: number, oldValue: number, filler?:string) {
    const formData = {
      aNum,
      file_Id,
      newValue: docTypeId.toString() + (!!filler ? "&&&&"+filler : ""),
      oldValue
    }

    const httpParams = this.httpService.createHttpParams(formData);
    const options = { headers: this.httpService.formHeaders };
    const url = '/change-file-attribute/type-of-document'

    return this.httpService.Post<any>(url, httpParams, options);
  }

  downloadCaseDocket(aNum: string) {
    const options = { headers: this.httpService.formHeaders, responseType: 'blob' };
    const url = '/download-offline-cds/' + aNum;

    return this.httpService.Post<Blob>(url, new HttpParams(), options);
  }

  validateDmInfo(aNum: string, dmNum: string) {
    const formData = {
      aNum,
      id: dmNum
    }

    const httpParams = this.httpService.createHttpParams(formData);
    const options = { headers: this.httpService.formHeaders };
    const url = '/validateDmInfo'

    return this.httpService.Post<DMNumberCheckResponse>(url, httpParams, options);
  }

  updateDmInfo(aNum: string, dmNum: string) {
    const formData = {
      aNum,
      id: dmNum
    }

    const httpParams = this.httpService.createHttpParams(formData);
    const options = { headers: this.httpService.formHeaders };
    const url = '/updateDmInfo'

    return this.httpService.Post<ProtestInfo>(url, httpParams, options);
  }

  loadDocket(a_No: string, refresh = false) {
    // Sub already has info loaded
    if (!refresh && this.docketSub.value && this.docketSub.value.protestInfo.a_No == a_No)
      return;
    else
      this.docketSub.next(undefined);

    const form = new FormData();
    form.append("a_No", a_No);
    this.httpService.Post<AttorneyInfoResponse>('/casedocketsheet/', form).pipe(
      catchError(err => {
        this.docketSub.next(undefined);
        return throwError(err);
      })
    ).subscribe(response => {
      if (response) {
        this.docketSub.next(response);
      } else {
        this.docketSub.next(undefined);
      }
    });
  }

  clearDocket() {
    this.docketSub.next(undefined);
  }

  onEmailPref() {
    if (this.docketSub.value) {
      let protestInfo = this.docketSub.value.protestInfo;

      let message = "";
      if (protestInfo.casedocket_email_preferences == 'N') {
        message = "Email notifications are currently inactive for this case. Do you want to receive email notifications for this case?";
      } else {
        message = "<b>Warning</b> - If you turn off email notification for this case, it is your responsibility to review the docket for any new filings.<br><br>" +
          "Email notifications are currently active for this case.  Do you want to stop receiving email notifications for this case?";
      }

      this.util.displayDialogMessage("Email Preferences", message,
        {confirmText: "Yes", cancelBtn: true, cancelText: "No"})
        .subscribe(res => {
          if (res) {
            const cdep = protestInfo.casedocket_email_preferences == 'Y' ? 'N' : 'Y';

            this.httpService.Get<User>('/update-email-preferences?aNum=' + protestInfo.a_No + '&value=' + cdep).pipe(
              catchError(err => {
                console.log('Handling error locally.', err);
                return EMPTY;
              })
            ).subscribe(response => {
              console.log(response);
              protestInfo.casedocket_email_preferences = cdep;
            });
          }
        });
    }
  }

  onComplete(text: CaseCompleteState, protestInfo: ProtestInfo) {
    switch (text) {
      case CaseCompleteState.Complete:
        this.completeZip(protestInfo);
        break;
      case CaseCompleteState.Final:
        this.enterDM(protestInfo);
        break;
      case CaseCompleteState.Pending:
        this.pendingVerification(protestInfo);
        break;
      case CaseCompleteState.Verify:
        this.verifyDM(protestInfo);
        break;
      default:
        break;
    }
  }

  private completeZip(protestInfo: ProtestInfo) {
    // TODO will need interaction blocking spinner to prevent navigation
    this.util.displayDialogMessage("Complete Case", "Do you want to close the docket and create a zip file of all case pleadings?",
      { confirmText: "Yes", confirmButtonColor: "warn", cancelBtn: true }).subscribe(res => {
        if (res) {
          const form = new FormData();
          form.append("aNum", protestInfo.a_No);
          this.httpService.Post<CompleteAndDMResponse>('/case-completed', form).subscribe(response => {
            if (response) {
              if (response.error) {
                this.util.displayDialogMessage("Error", "Error: " + response.error + "<br>" +
                  "ErrorId: " + response.errorId + "<br>" + "Date: " + Date.now());
              } else if (response.isSuccess) {
                this.loadDocket(protestInfo.a_No, true);
                this.util.displayDialogMessage("Zip Complete", "You have successfully created the zipfile. The case has not yet been completed.<br>" +
                  "Please follow the instructions in the EPDS GAO user manual under the section <strong>Case Completion Workflow</strong>.");
              } else {
                this.util.displayDialogMessage("Zip file creation in progress", "Please wait until zip file is created successfully.");
              }
            }
          });
        }
      });
  }

  private enterDM(protestInfo: ProtestInfo) {
    this.util.displayDialogMessage(CaseCompleteState.Final, "Enter DM #:",
      { cancelBtn: true, getResponse: true, responseReq: true }).subscribe(res => {
        if (res) {
          const form = new FormData();
          form.append("id", res.response);
          this.httpService.Post<ValidateDmInfoResponse>('/validateDmInfo', form).subscribe(valRes => {
            if (valRes && valRes.isExists) {
              this.util.displayDialogMessage("Error", "The DM# you have entered already exists.  Please check the DM# and try again.");
            } else {
              form.append("aNum", protestInfo.a_No);
              this.httpService.Post<CompleteAndDMResponse>('/updateDmInfo', form).subscribe(upRes => {
                if (upRes.error) {
                  this.util.displayDialogMessage("Error", "Error: " + upRes.error + "<br>" +
                    "ErrorId: " + upRes.errorId + "<br>" + "Date: " + Date.now());
                } else {
                  protestInfo.caseCompletionStatus.dmNumber = res.response;
                  protestInfo.caseCompletionStatus.isDmEntered = true;
                  this.sidebarService.setNavByUser(this.stateService.user.role_id, protestInfo);
                }
              });
            }
          });
        }
      });
  }

  private pendingVerification(protestInfo: ProtestInfo) {
    this.util.displayDialogMessage(CaseCompleteState.Pending, "DM# verification is still pending.");
  }

  private verifyDM(protestInfo: ProtestInfo) {
    this.util.displayDialogMessage(CaseCompleteState.Verify, "Verify DM #:",
      { cancelBtn: true, getResponse: true, responseReq: true }).subscribe(res => {
        if (res) {
          const form = new FormData();
          form.append("id", res.response);
          form.append("aNum", protestInfo.a_No);
          this.httpService.Post<VerifyDmInfoResponse>('/verifyDmInfo', form).subscribe(response => {
            if (response) {
              if (response.hasOpenChild) {
                let message = "The case you're trying to verify (" + protestInfo.a_No + ", " + protestInfo.b_No + ", " +
                  protestInfo.case_Type + ", " + protestInfo.company_Name + ") has uncompleted cases referencing the files in this case." +
                  "They will need to be completed to zip up the files under those cases.<br><br>"
                message += " Open cases: <br>"
                response.protestInfoList.forEach(pi => {
                  message += pi.a_No + ", " + pi.b_No + ", " + pi.case_Type + ", " + pi.company_Name + "<br>";
                });
                this.util.displayDialogMessage("Warning", message);
              } else if (!response.isEqual) {
                this.util.displayDialogMessage("Error", "The DM# you have entered does not match.<br>" +
                  "Prior Code: " + response.dbDMNumber + "<br>Verification Code: " + res.response + "<br>Please check the DM# and try again.");
              } else {
                protestInfo.case_Status = "COMPLETE";
                protestInfo.caseCompletionStatus.isDmVerfied = true;
                this.sidebarService.setNavByUser(this.stateService.user.role_id, protestInfo);
              }
            }
          });
        }
      });
  }

  changeFileAttribute(request: {
    file_Id: number,
    newValue: any
  }, typeOfChange: string) {
    const httpParams = this.httpService.createHttpParams(request);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const options = { ...headers };
    return this.httpService.Post<AttorneyInfoResponse>('/change-file-attribute/' + typeOfChange, httpParams, options);
  }

  redirectToCaseDocket(protest: ProtestInfo) {
    this.docketSub.next(undefined);
    this.checkCaseAccessRequestStatus(protest, UserRoleString[this.stateService.user.role_id] + '/case-docketsheet/' + protest.a_No)
  }

  checkCaseAccessRequestStatus(protest: ProtestInfo, redirectPath?: string) {
    switch(protest.caseAccessRequestStatus) {
      case "P": {
        this.util.displayDialogMessage("Request Pending for " + protest.b_No,
          "Your " + protest.caseAccessRequestType + " is pending.  You will only have access to this case's docket if your request is granted by GAO.");
        break;
      }
      case "D": {
        //(parentANo || caseInfObj.a_No, caseInfObj.fileInfoSubmissionDate ||  caseInfObj.deniedDate, caseInfObj.docTypeId || caseInfObj.deniedIndicatingDocTypeId, "Y")
        this.caseAccessReqDeniedFileInfo(protest.parent_A_No || protest.a_No, protest.deniedDate,
          protest.deniedIndicatingDocTypeId, "Y").subscribe(res => {
          if (res) {
            this.util.displayDialogMessage("Request Denied for " + protest.b_No,
              "Your " + protest.caseAccessRequestType + " is denied.  Please refer to the PDF file included here for additional information.<br><br>" +
              "<a href='" + this.util.getBaseURL() + "/epds/downloaddashboardfile?fileId=" + res.file_InfoList[0].file_Id + "' target='_blank'>" + res.file_InfoList[0].fileName + "</a>");
          }
        });
        break;
      }
      default: {
        if (redirectPath) this.router.navigate([redirectPath] );
      }
    }
  }

  caseAccessReqDeniedFileInfo(protestId: string, submissionDate: string, doc_Type_Id: number, fileAlert: string) {
    const formData = {
      protestId, submissionDate, doc_Type_Id, fileAlert
    };

    const httpParams = this.httpService.createHttpParams(formData);
    const options = {headers: this.httpService.formHeaders};
    return this.httpService.Post<CaseAccessResponse>('/case-access-request-denied-file-info', httpParams, options);
  }

  validateJoin(b_Num: string, parentBnum: string) {
    const formData = {
      b_Num, parentBnum
    };

    const httpParams = this.httpService.createHttpParams(formData);
    const options = {headers: this.httpService.formHeaders};
    return this.httpService.Post<JoinValidateResponse>('/validate-join', httpParams, options);
  }

  joinCases(aNum: string, listOfBNumbers: string, newValue: string|null, oldValue: string|null) {
    const formData = {
      aNum, listOfBNumbers, newValue, oldValue
    };

    const httpParams = this.httpService.createHttpParams(formData);
    const options = {headers: this.httpService.formHeaders};
    return this.httpService.Post<JoinCaseResponse>('/change-protest_Info-attribute/joinCases', httpParams, options);
  }

  unJoinCases(aNum: string, listOfBNumbers: string, newValue: string|null, oldValue: string|null) {
    const formData = {
      aNum, listOfBNumbers, newValue, oldValue
    };

    const httpParams = this.httpService.createHttpParams(formData);
    const options = {headers: this.httpService.formHeaders};
    return this.httpService.Post<JoinCaseResponse>('/change-protest_Info-attribute/unJoinCases', httpParams, options);
  }
}
